import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle } from "@material-ui/core";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInputValidate.jsx";
import { clone, get } from "lodash";
import moment from "moment";

const REQUIRED_FIELDS = [
    "lease_start_date",
    "rent_commencement",
    "occupancy_date",
    "lease_end_date",
    "date_lease_signed",
];

const parseDollar = (s) => {
    if (typeof s === "number") return s;
    if (!s) return 0;
    const cleaned = String(s).replace(/[^0-9.\-]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

const computeYearRows = (data) => {
    const basedOff = data.lease_ranges_based_off === "Rent Commencement Date"
        ? data.rent_commencement
        : data.lease_start_date;
    if (!basedOff || !data.lease_end_date) return [];
    const start = moment(basedOff, "MM/DD/YYYY", true);
    const end = moment(data.lease_end_date, "MM/DD/YYYY", true);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

    const rows = [];
    let cursorStart = start.clone();
    let yearNum = 1;
    while (cursorStart.isSameOrBefore(end)) {
        let cursorEnd = cursorStart.clone().add(12, "M").subtract(1, "d");
        if (cursorEnd.isAfter(end)) cursorEnd = end.clone();
        rows.push({
            year_num: yearNum,
            date_start: cursorStart.format("MM/DD/YYYY"),
            date_end: cursorEnd.format("MM/DD/YYYY"),
            rent_amount: "",
            commission_rate: "",
        });
        cursorStart = cursorEnd.clone().add(1, "d");
        yearNum += 1;
        if (yearNum > 50) break;
    }
    return rows;
};

const readRowsFromData = (data) => {
    const lt = data.lease_term || [];
    const ab = data.abated_rent_ranges || [];
    if (lt.length === 0 && ab.length === 0) return [];
    const length = Math.max(lt.length, ab.length);
    const rows = [];
    for (let i = 0; i < length; i++) {
        const ltYear = lt[i] || null;
        const abYear = ab[i] || null;
        const ltRange = ltYear && ltYear.ranges && ltYear.ranges[0] ? ltYear.ranges[0] : null;
        const abRange = abYear && abYear.ranges && abYear.ranges[0] ? abYear.ranges[0] : null;

        let date_start = "";
        let date_end = "";
        if (ltYear && ltYear.year_start) date_start = moment.unix(ltYear.year_start).format("MM/DD/YYYY");
        if (ltYear && ltYear.year_end) date_end = moment.unix(ltYear.year_end).format("MM/DD/YYYY");
        if (!date_start && abYear && abYear.year_start) date_start = moment.unix(abYear.year_start).format("MM/DD/YYYY");
        if (!date_end && abYear && abYear.year_end) date_end = moment.unix(abYear.year_end).format("MM/DD/YYYY");
        if (ltRange && ltRange.date1) date_start = ltRange.date1;
        if (ltRange && ltRange.date2) date_end = ltRange.date2;
        if (!ltRange && abRange && abRange.date1) date_start = abRange.date1;
        if (!ltRange && abRange && abRange.date2) date_end = abRange.date2;

        let rent_amount = "";
        let commission_rate = "";
        if (ltRange && ltRange.value !== undefined && ltRange.value !== "") {
            rent_amount = String(ltRange.value);
            if (ltRange.commissionRate !== undefined && ltRange.commissionRate !== "") {
                commission_rate = String(ltRange.commissionRate);
            }
        } else if (abRange && abRange.value !== undefined && abRange.value !== "") {
            rent_amount = "-" + String(abRange.value);
            if (abRange.commissionRate !== undefined && abRange.commissionRate !== "") {
                commission_rate = String(abRange.commissionRate);
            }
        }

        rows.push({
            year_num: (ltYear && ltYear.year_num) || (abYear && abYear.year_num) || i + 1,
            date_start,
            date_end,
            rent_amount,
            commission_rate,
        });
    }
    return rows;
};

const LeaseTermGenerator = (props) => {
    const { data, setData, updateDataObj } = props;
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [startingRent, setStartingRent] = useState("");
    const [annualIncrease, setAnnualIncrease] = useState("");

    const missing = REQUIRED_FIELDS.filter((f) => !get(data, f));
    const disabled = missing.length > 0;

    const based_off_value = data.lease_ranges_based_off === "Rent Commencement Date"
        ? "Rent Commencement Date" : "Lease Start Date";
    const rate_type_value = data.rate_type || "";

    useEffect(() => {
        if (open) {
            const saved = readRowsFromData(data);
            const computed = computeYearRows(data);
            if (saved.length > 0) {
                // If the lease end date (or other date inputs) changed since the
                // rows were last saved, regenerate the year ranges from the
                // current dates but keep any rent/commission amounts the user
                // already entered. Otherwise show the saved rows as-is.
                const savedEnd = saved[saved.length - 1].date_end;
                if (computed.length > 0 && savedEnd !== data.lease_end_date) {
                    const merged = computed.map((row, i) => {
                        const s = saved[i];
                        return s
                            ? { ...row, rent_amount: s.rent_amount, commission_rate: s.commission_rate }
                            : row;
                    });
                    setRows(merged);
                } else {
                    setRows(saved);
                }
            } else {
                setRows(computed);
            }
        }
    }, [open, data.lease_end_date, data.lease_start_date, data.rent_commencement, data.lease_ranges_based_off]);

    const updateCell = (idx, field, value) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
        setRows(next);
    };

    const sortRows = (arr) => {
        return arr.slice().sort((a, b) => {
            const ya = Number(a.year_num) || 0;
            const yb = Number(b.year_num) || 0;
            if (ya !== yb) return ya - yb;
            const da = moment(a.date_start, "MM/DD/YYYY", true);
            const db = moment(b.date_start, "MM/DD/YYYY", true);
            if (!da.isValid() || !db.isValid()) return 0;
            return da.unix() - db.unix();
        });
    };

    const reflowRowOnBlur = (idx) => {
        const r = rows[idx];
        if (!r) return;
        if (!r.year_num || !r.date_start || !r.date_end) return;
        const sorted = sortRows(rows);
        const renumbered = sorted.map((row, i) => ({ ...row, year_num: i + 1 }));
        setRows(renumbered);
    };

    const addRow = () => {
        const last = rows[rows.length - 1];
        const nextStart = last
            ? moment(last.date_end, "MM/DD/YYYY").add(1, "d").format("MM/DD/YYYY")
            : (data.lease_start_date || "");
        const nextEnd = last
            ? moment(last.date_end, "MM/DD/YYYY").add(1, "y").format("MM/DD/YYYY")
            : (data.lease_end_date || "");
        setRows([
            ...rows,
            {
                year_num: rows.length + 1,
                date_start: nextStart,
                date_end: nextEnd,
                rent_amount: "",
                commission_rate: "",
            },
        ]);
    };

    const deleteRow = (idx) => {
        const next = rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, year_num: i + 1 }));
        setRows(next);
    };

    const autoFillFromIncrease = () => {
        const start = parseDollar(startingRent);
        const inc = parseDollar(annualIncrease);
        if (!start) return;
        const isPct = rate_type_value === "Fixed Percentage";
        const next = rows.map((r, i) => {
            let amount = start;
            if (isPct) {
                amount = start * Math.pow(1 + inc / 100, i);
            } else {
                amount = start + inc * i;
            }
            return { ...r, rent_amount: amount.toFixed(2) };
        });
        setRows(next);
    };

    const writeToData = (rowsToWrite) => {
        const lease_term = [];
        const abated_rent_ranges = [];
        rowsToWrite.forEach((r, idx) => {
            const year_index = String(idx);
            const rentAmountRaw = r.rent_amount;
            const rentAmount = parseDollar(rentAmountRaw);
            const hasRent = rentAmountRaw !== "" && rentAmountRaw !== null && rentAmountRaw !== undefined && !isNaN(rentAmount);
            const start = moment(r.date_start, "MM/DD/YYYY", true);
            const end = moment(r.date_end, "MM/DD/YYYY", true);
            const year_start = start.isValid() ? start.unix() : null;
            const year_end = end.isValid() ? end.unix() : null;
            const rateNum = r.commission_rate === "" || r.commission_rate === null || r.commission_rate === undefined
                ? ""
                : parseFloat(r.commission_rate);
            const commissionRate = rateNum === "" || isNaN(rateNum) ? "" : rateNum;

            const ltYear = {
                year_num: Number(r.year_num) || idx + 1,
                year_start,
                year_end,
                ranges: [],
            };
            const abYear = {
                year_num: Number(r.year_num) || idx + 1,
                year_start,
                year_end,
                ranges: [],
            };

            if (hasRent && rentAmount < 0) {
                abYear.ranges.push({
                    year_index,
                    range_index: 0,
                    date1: r.date_start,
                    date2: r.date_end,
                    value: String(Math.abs(rentAmount)),
                    commissionRate,
                });
            } else if (hasRent) {
                ltYear.ranges.push({
                    year_index,
                    range_index: 0,
                    date1: r.date_start,
                    date2: r.date_end,
                    value: String(rentAmount),
                    commissionRate,
                });
            }
            lease_term.push(ltYear);
            abated_rent_ranges.push(abYear);
        });

        console.log('[LeaseTermGenerator] writing', { lease_term, abated_rent_ranges });
        if (typeof setData === "function") {
            const next = clone(data);
            next.lease_term = lease_term;
            next.abated_rent_ranges = abated_rent_ranges;
            setData(next);
        } else if (typeof updateDataObj === "function") {
            updateDataObj("lease_term", lease_term);
            updateDataObj("abated_rent_ranges", abated_rent_ranges);
        }
    };

    const saveAndClose = () => {
        const sorted = sortRows(rows).map((r, i) => ({ ...r, year_num: i + 1 }));
        writeToData(sorted);
        setOpen(false);
    };

    const labelCell = { padding: "4px 8px", fontWeight: "bold", border: "1px solid #ccc", background: "#f5f5f5" };
    const valueCell = { padding: "4px 8px", border: "1px solid #ccc" };
    const editableCell = { padding: "2px 4px", border: "1px solid #ccc", background: "#fff9c4" };
    const gridHeaderCell = { padding: "4px 8px", fontWeight: "bold", background: "#da2229", color: "#fff", border: "1px solid #ccc", textAlign: "center" };

    return (
        <>
            {/* trigger */}
            <Button
                color="primary"
                disabled={disabled}
                onClick={() => { if (!disabled) setOpen(true); }}
                style={{ marginTop: 8 }}
            >
                Lease Term Generator
            </Button>
            {disabled && (
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                    Fill in Lease Start Date, Rent Commencement, Occupancy Date, Lease End Date, and Date Lease Signed to enable.
                </div>
            )}

            {/* popup */}
            {open && (
                <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} maxWidth="lg">
                    <DialogTitle>
                        <div style={{ textAlign: "center", fontWeight: "bold" }}>Lease Term Generator</div>
                    </DialogTitle>
                    <div style={{ padding: 20 }}>
                        {/* summary header */}
                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
                            <tbody>
                                <tr>
                                    <td style={labelCell}>Based Off</td>
                                    <td style={valueCell}>{based_off_value}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Lease Start Date</td>
                                    <td style={valueCell}>{data.lease_start_date || ""}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Lease End Date</td>
                                    <td style={valueCell}>{data.lease_end_date || ""}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Rent Commencement Date</td>
                                    <td style={valueCell}>{data.rent_commencement || ""}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Occupancy Date</td>
                                    <td style={valueCell}>{data.occupancy_date || ""}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Date Lease Signed</td>
                                    <td style={valueCell}>{data.date_lease_signed || ""}</td>
                                    <td style={labelCell} colSpan={2}></td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Rental Increases</td>
                                    <td style={valueCell}>{rate_type_value || "Fixed Amount"}</td>
                                    <td style={labelCell}>Starting Rent Amount</td>
                                    <td style={editableCell}>
                                        <CustomInput
                                            value={startingRent}
                                            onChange={(e) => setStartingRent(e.target.value)}
                                            isCurrency
                                            startAdornment="$"
                                        />
                                    </td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td style={labelCell}>Lease Term</td>
                                    <td style={valueCell}>
                                        {rows.length} Year{rows.length === 1 ? "" : "s"}
                                    </td>
                                    <td style={labelCell}>
                                        {rate_type_value === "Fixed Percentage" ? "Annual Increase %" : "Annual Increase Amount"}
                                    </td>
                                    <td style={editableCell}>
                                        <CustomInput
                                            value={annualIncrease}
                                            onChange={(e) => setAnnualIncrease(e.target.value)}
                                            isCurrency={rate_type_value !== "Fixed Percentage"}
                                            startAdornment={rate_type_value === "Fixed Percentage" ? "" : "$"}
                                            endAdornment={rate_type_value === "Fixed Percentage" ? "%" : ""}
                                        />
                                    </td>
                                    <td style={valueCell}></td>
                                </tr>
                                <tr>
                                    <td colSpan={5} style={{ padding: 4 }}>
                                        <Button color="primary" size="sm" onClick={autoFillFromIncrease} disabled={!startingRent}>
                                            Auto-fill rent from Starting + Annual Increase
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* year grid */}
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={gridHeaderCell}></th>
                                    <th style={gridHeaderCell}>YEAR</th>
                                    <th style={gridHeaderCell}>RENT AMOUNT</th>
                                    <th style={gridHeaderCell}>Commission Rate</th>
                                    <th style={gridHeaderCell}>Date Start</th>
                                    <th style={gridHeaderCell}>Date End</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`ltg-row-${i}`}>
                                        <td style={{ ...valueCell, textAlign: "center", width: 28, cursor: "pointer", color: "#da2229", fontWeight: "bold" }}
                                            onClick={() => deleteRow(i)}
                                            title="Delete row"
                                        >
                                            x
                                        </td>
                                        <td style={{ ...editableCell, textAlign: "center" }}>
                                            <CustomInput
                                                value={r.year_num}
                                                onChange={(e) => updateCell(i, "year_num", e.target.value)}
                                                onBlur={() => reflowRowOnBlur(i)}
                                            />
                                        </td>
                                        <td style={editableCell}>
                                            <CustomInput
                                                value={r.rent_amount}
                                                onChange={(e) => updateCell(i, "rent_amount", e.target.value)}
                                                isCurrency
                                                startAdornment="$"
                                            />
                                        </td>
                                        <td style={editableCell}>
                                            <CustomInput
                                                value={r.commission_rate}
                                                onChange={(e) => updateCell(i, "commission_rate", e.target.value)}
                                                endAdornment="%"
                                            />
                                        </td>
                                        <td style={editableCell}>
                                            <CustomInput
                                                value={r.date_start}
                                                onChange={(e) => updateCell(i, "date_start", e.target.value)}
                                                onBlur={() => reflowRowOnBlur(i)}
                                            />
                                        </td>
                                        <td style={editableCell}>
                                            <CustomInput
                                                value={r.date_end}
                                                onChange={(e) => updateCell(i, "date_end", e.target.value)}
                                                onBlur={() => reflowRowOnBlur(i)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan={6} style={{ padding: 4 }}>
                                        <Button color="primary" size="sm" onClick={addRow}>
                                            + Add Row
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* footer actions */}
                        <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "flex-end" }}>
                            <Button color="white" onClick={saveAndClose}>
                                Go Back
                            </Button>
                            <Button color="primary" onClick={saveAndClose}>
                                Generate Lease Terms
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    );
};

export default LeaseTermGenerator;
